// @flow
import React, { type Node, Fragment, useRef, useCallback } from 'react';
import cn from 'classnames/bind';
import isEmpty from 'ramda/es/isEmpty';

import CSSSticky from 'app/common/ui-components/css-sticky';
import SimpleDate from 'app/dataProviders/SimpleDate';
import { useHoverByClass } from 'app/common/ui-components/table';
import HScrollbarsWShadow, { useSyncScrolls } from 'app/common/ui-components/h-scrollbars-w-shadow';
import Loading from 'app/common/components/Loader';
import type { Discipline } from 'app/dataTypes/Discipline.types';

import TableHeadFixed from './TableHeadFixed';
import TableHeadDays from './TableHeadDays';
import TableFixed from './TableFixed';
import TableDays from './TableDays';
import TableHeadAverage from './TableHeadAverage';
import TableHeadFinal from './TableHeadFinal';
import TableSummary from './TableSummary';
import useMonthMarks from './useMonthMarks';
import useMonthDays from './useMonthDays';
import useAverageMarks from './useAverageMarks';
import useFinalMarks from './useFinalMarks';

import styles from './TableMarks.scss';


const cx = cn.bind(styles);

type Props = {
  date: SimpleDate,
  klassId: string,
  studentId: string,
  disciplines: Array<Discipline>,
  onUpdateDIsciplines: (Array<Discipline>) => void,
}

const TableMarks = ({ date, klassId, studentId, disciplines, onUpdateDIsciplines }: Props): Node => {
  const params = { klassId, studentId, date };
  const [monthMarks, loading] = useMonthMarks(params, onUpdateDIsciplines);
  const days = useMonthDays(date);

  const averageMarks = useAverageMarks(params);
  const finalMarks = useFinalMarks(params);
  const hasAverage = !isEmpty(averageMarks);
  const hasFinal = !isEmpty(finalMarks);
  const hasDoubleSummary = hasAverage && hasFinal;
  const hasSingleSummary = (hasAverage || hasFinal) && !hasDoubleSummary;

  const scrollableClassName = cx(styles.hScrollableContent, {
    hasSingleSummary,
    hasDoubleSummary,
  });

  const summaryClassName = cx(styles.summary, {
    summaryEmpty: !hasAverage && !hasFinal,
    summarySingle: hasSingleSummary,
    summaryDouble: hasDoubleSummary,
  });

  const [onMouseEnterCell, onMouseLeaveCell] = useHoverByClass('hover_v_');
  const [onMouseEnterRow, onMouseLeaveRow] = useHoverByClass('hover_h_');
  const [topScrollerProps, middleScrollerProps] = useSyncScrolls();

  const scrollerRef = useRef<HTMLElement | null>(null);
  const handleChangeContentSize = useCallback((isContentBiggerThanContainer: boolean) => {
    const node = scrollerRef.current;
    if (node) {
      if (isContentBiggerThanContainer) {
        node.classList.remove(styles.hidden);
      } else {
        node.classList.add(styles.hidden);
      }
    }
  }, []);

  return (
    <Fragment>
      <CSSSticky
        number={3}
        className={cn(styles.stickable, styles.scrollableContainer)}
        shadowClassName={styles.shadow}
      >
        <TableHeadFixed
          className={styles.fixed}
        />
        <div className={scrollableClassName}>
          <HScrollbarsWShadow
            hideVerticalScrollBar
            hideHorizontalScrollBar
            {...topScrollerProps}
          >
            <TableHeadDays
              className={styles.scrollable}
              onMouseEnterCell={onMouseEnterCell}
              onMouseLeaveCell={onMouseLeaveCell}
              days={days}
            />
          </HScrollbarsWShadow>
        </div>
        <div className={summaryClassName}>
          {hasAverage
            && <TableHeadAverage className={styles.summaryColumn} />}
          {hasFinal
            && <TableHeadFinal className={styles.summaryColumn} />}
        </div>
      </CSSSticky>

      <div className={styles.scrollableContainer}>
        <TableFixed
          className={styles.fixed}
          disciplines={disciplines}
          onMouseEnterRow={onMouseEnterRow}
          onMouseLeaveRow={onMouseLeaveRow}
          loading={loading}
        />
        <div className={scrollableClassName}>
          <HScrollbarsWShadow
            hideVerticalScrollBar
            onChangeContentSize={handleChangeContentSize}
            {...middleScrollerProps}
          >
            <TableDays
              className={styles.scrollable}
              disciplines={disciplines}
              monthMarks={monthMarks}
              days={days}
              onMouseEnterCell={onMouseEnterCell}
              onMouseLeaveCell={onMouseLeaveCell}
              onMouseEnterRow={onMouseEnterRow}
              onMouseLeaveRow={onMouseLeaveRow}
              loading={loading}
            />
          </HScrollbarsWShadow>

          {loading
            && <Loading className={styles.loading} />}
        </div>

        <div className={summaryClassName}>
          {hasAverage
            && <TableSummary
              className={styles.summaryColumn}
              disciplines={disciplines}
              data={averageMarks}
              onMouseEnterRow={onMouseEnterRow}
              onMouseLeaveRow={onMouseLeaveRow}
            />}
          {hasFinal
            && <TableSummary
              className={styles.summaryColumn}
              disciplines={disciplines}
              data={finalMarks}
              onMouseEnterRow={onMouseEnterRow}
              onMouseLeaveRow={onMouseLeaveRow}
            />}
        </div>
      </div>
    </Fragment>
  );
};

export default TableMarks;
